'use client';

import { FORM_ID_FIELD, FORM_LOCALE_FIELD, type FormDto } from '../form.types';
import { Button, Field, Fieldset, Label } from '@headlessui/react';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import type { PortableTextBlock } from '@portabletext/types';
import {
    FormFieldCheckbox,
    FormFieldColor,
    FormFieldDate,
    FormFieldDateTime,
    FormFieldFile,
    FormFieldHidden,
    FormFieldInput,
    FormFieldNumber,
    FormFieldRadio,
    FormFieldRange,
    FormFieldSelect,
    FormFieldTextarea,
    FormFieldTime,
} from './formfields';
import { FormFeedback } from './FormFeedback';
import { FormSpamGuard } from './FormSpamGuard';
import { LABEL_CLASS } from './formfields/field.styles';
import { detectSpam } from '../spam-guard';
import { useFillDuration } from '../useFillDuration';
import { PortableTextRenderer } from '@/components/content';
import { useRouteModalOptional } from '@/components/ui/patterns/UiRouteModal';
import { useDictionary, useLocale } from '@/components/providers';
import { cn } from '@/lib/utils/tw-merge';

const SUBMIT_BUTTON_CLASS = 'btn btn-primary btn-lg disabled:pointer-events-none disabled:opacity-60';

/** Only the fields the privacy link needs; the block fragment projects just these.
 *  `language` comes from the referenced page so the link keeps its own locale. */
type PrivacyPolicyLink = { slug?: string; title?: string; language?: string };

export function DynamicForm({
    form,
    privacyPolicyPage,
    content,
    contentClassName,
    successText,
    errorText,
    framed = false,
}: {
    form: FormDto;
    privacyPolicyPage?: PrivacyPolicyLink;
    /** Intro richText shown above the form; hidden while a result panel shows. */
    content?: PortableTextBlock[];
    contentClassName?: string;
    successText?: PortableTextBlock[];
    errorText?: PortableTextBlock[];
    /** True when a visible frame (TabForm card) already provides the anchor the
     *  countdown bar attaches to; then we don't add our own. */
    framed?: boolean;
}) {
    const searchParams = useSearchParams();
    const locale = useLocale();
    const { form: formCopy } = useDictionary();
    // Null outside a modal → drives the button label and dismiss behaviour.
    const modal = useRouteModalOptional();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const stampFillDuration = useFillDuration();

    // The countdown bar is position:absolute and pins to the nearest positioned
    // ancestor: the modal panel (in a modal) or the TabForm card (framed). With
    // neither, we make our own wrapper the anchor so the bar can't escape to the
    // viewport.
    const inModal = modal !== null;
    const selfFrame = !inModal && !framed;

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const formElement = e.currentTarget;
            const formData = new FormData(formElement);
            stampFillDuration(formData);

            // Honeypot filled, no JS proof or submitted too fast: show the standard
            // error and keep the values, exactly like a failed request. The route
            // checks the same signals again, since a bot can skip this code path.
            if (detectSpam(formData)) {
                setSubmitStatus('error');
                return;
            }

            const response = await fetch('/api/send-form', {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                // Reset up front so the emptied form is ready behind the success panel.
                setSubmitStatus('success');
                formElement.reset();
            } else {
                // Keep the entered values on error (no reset) so the user can retry.
                setSubmitStatus('error');
            }
        } catch {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Success inside a modal closes it (animated → router.back); every other case
    // returns to the form. On error the form stays mounted (only hidden), so its
    // values survive; on success handleSubmit already reset it.
    const handleDismiss = useCallback(() => {
        if (submitStatus === 'success' && modal) {
            modal.close();
        } else {
            setSubmitStatus('idle');
        }
    }, [submitStatus, modal]);

    // Lay fields out in a 2-column grid; textareas (and a trailing odd field)
    // span the full width so the grid never leaves a dangling half-row.
    const getFieldWidths = () => {
        if (!form.fields) return new Map<string, boolean>();

        const widths = new Map<string, boolean>();
        let currentRowSpan = 0;

        form.fields.forEach((field, index) => {
            if (!field._key) return;

            const isFullWidth = field.type === 'textarea';

            if (isFullWidth) {
                if (currentRowSpan === 1) {
                    const prevField = form.fields![index - 1];
                    if (prevField?._key) {
                        widths.set(prevField._key, true);
                    }
                }

                widths.set(field._key, true);
                currentRowSpan = 0;
            } else {
                currentRowSpan++;

                if (currentRowSpan === 2) {
                    currentRowSpan = 0;
                }

                widths.set(field._key, false);
            }
        });

        if (currentRowSpan === 1 && form.fields.length > 0) {
            const lastField = form.fields[form.fields.length - 1];
            if (lastField?._key) {
                widths.set(lastField._key, true);
            }
        }

        return widths;
    };

    const fieldWidths = getFieldWidths();

    const dismissLabel = submitStatus === 'success' && modal ? formCopy.dismissModal : formCopy.backToForm;

    return (
        // selfFrame: no overflow-hidden — the bar is already full-width/flush, and
        // clipping here would cut the edge fields' focus glow (no card padding).
        <div className={cn(selfFrame && 'relative')}>
            {/* Intro sits above the form and is replaced by the result panel. */}
            {content && submitStatus === 'idle' ? (
                <PortableTextRenderer value={content} className={contentClassName} />
            ) : null}

            {/* Prefixed so the bare form id stays free for TabForm's block-top jump markers.
                Kept mounted (only hidden) while a result shows, so an error keeps the entered values. */}
            <form id={`form-${form.id}`} onSubmit={handleSubmit} hidden={submitStatus !== 'idle'}>
                <input type="hidden" name={FORM_ID_FIELD} defaultValue={form.id} />
                {/* The slug alone is ambiguous across languages, and the route needs the
                    locale for the notification mail's subject. */}
                <input type="hidden" name={FORM_LOCALE_FIELD} defaultValue={locale} />

                <Fieldset>
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                        {form.fields?.map((field) => {
                            const isFullWidth = fieldWidths.get(field._key!) ?? false;

                            return (
                                <Field key={field._key} className={cn(isFullWidth && 'md:col-span-2')}>
                                    {field.type !== 'hidden' && (
                                        <Label className={LABEL_CLASS}>
                                            {field.label}
                                            {!field.required && field.label && (
                                                <span className="form-label-optional">{formCopy.optional}</span>
                                            )}
                                        </Label>
                                    )}
                                    {field.type === 'textarea' && <FormFieldTextarea field={field} />}
                                    {field.type === 'select' && <FormFieldSelect field={field} />}
                                    {field.type === 'radio' && <FormFieldRadio field={field} />}
                                    {field.type === 'checkbox' && <FormFieldCheckbox field={field} />}
                                    {field.type === 'date' && <FormFieldDate field={field} />}
                                    {field.type === 'datetime-local' && <FormFieldDateTime field={field} />}
                                    {field.type === 'time' && <FormFieldTime field={field} />}
                                    {field.type === 'range' && <FormFieldRange field={field} />}
                                    {field.type === 'number' && <FormFieldNumber field={field} />}
                                    {field.type === 'file' && <FormFieldFile field={field} />}
                                    {field.type === 'color' && <FormFieldColor field={field} />}
                                    {field.type === 'hidden' && (
                                        <FormFieldHidden
                                            field={field}
                                            defaultValue={
                                                field.name ? (searchParams.get(field.name) ?? undefined) : undefined
                                            }
                                        />
                                    )}
                                    {(!field.type || ['text', 'email', 'tel', 'url'].includes(field.type)) && (
                                        <FormFieldInput field={field} />
                                    )}
                                </Field>
                            );
                        })}
                    </div>

                    {privacyPolicyPage?.slug && (
                        <p className="form-note mt-6">
                            <span>{formCopy.privacyNotice.before}</span>
                            <Link href={`/${privacyPolicyPage.language ?? locale}/m/${privacyPolicyPage.slug}`}>
                                {formCopy.privacyNotice.linkText}
                            </Link>
                            {formCopy.privacyNotice.after}
                        </p>
                    )}

                    {form.submitButton && (
                        <div className="mt-8 flex justify-start">
                            <Button type="submit" className={SUBMIT_BUTTON_CLASS} disabled={isSubmitting}>
                                {isSubmitting ? formCopy.submitting : form.submitButton.text}
                            </Button>
                        </div>
                    )}
                </Fieldset>
                <FormSpamGuard />
            </form>

            {submitStatus === 'success' && (
                <FormFeedback
                    variant="success"
                    content={successText}
                    buttonLabel={dismissLabel}
                    onDismiss={handleDismiss}
                    autoDismiss
                />
            )}

            {submitStatus === 'error' && (
                <FormFeedback
                    variant="error"
                    content={errorText}
                    buttonLabel={formCopy.backToForm}
                    onDismiss={handleDismiss}
                    autoDismiss
                />
            )}
        </div>
    );
}
